/**
 * Executive Dashboard - High-level security posture view for executives
 *
 * Designed for:
 * - CISOs preparing board reports
 * - Executives monitoring organizational risk
 * - Board members reviewing security posture
 * - External auditors seeking quick overview
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Shield,
  AlertTriangle,
  Building2,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  RefreshCw,
  ArrowLeft,
  ChevronRight,
  Award,
  Target,
  Users,
  Calendar,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

// ============================================================================
// TYPES
// ============================================================================

interface ExecutiveData {
  organization: {
    name: string
    website: string | null
  }
  generated_at: number
  compare_period: string
  posture: {
    score: number
    trend: 'improving' | 'stable' | 'declining'
    level: 'strong' | 'moderate' | 'needs_attention' | 'critical'
  }
  kpis: {
    compliance: {
      score: number
      total_controls: number
      compliant_controls: number
      trend: number
      trend_direction: 'up' | 'down' | 'stable'
    }
    risk: {
      posture_score: number
      total: number
      open: number
      critical: number
      high: number
      resolved_this_period: number
    }
    vendor: {
      health_score: number
      total: number
      assessed: number
      critical_risk: number
      high_risk: number
    }
    activity: {
      evidence_this_period: number
      evidence_trend: number
    }
  }
  top_risks: Array<{
    id: string
    title: string
    description: string
    severity: 'critical' | 'high' | 'medium' | 'low'
    risk_score: number
    category: string
    owner: string | null
    days_overdue: number | null
  }>
  overdue_controls: Array<{
    id: string
    control_number: string
    name: string
    framework: string
    days_overdue: number
    owner: string | null
  }>
  achievements: Array<{
    type: string
    title: string
    description: string
    icon: string
  }>
  frameworks: Array<{
    id: string
    name: string
    code: string
    compliance_percentage: number
    compliant_controls: number
    total_controls: number
    overdue_controls: number
    status: 'on_track' | 'needs_attention' | 'at_risk'
  }>
  trend: Array<{
    date: string
    evidence_count: number
  }>
}

type ComparePeriod = 'week' | 'month' | 'quarter' | 'year'

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getPostureLevelColor(level: string): string {
  switch (level) {
    case 'strong':
      return 'text-green-600'
    case 'moderate':
      return 'text-yellow-600'
    case 'needs_attention':
      return 'text-orange-600'
    case 'critical':
      return 'text-red-600'
    default:
      return 'text-gray-600'
  }
}

function getPostureLevelBg(level: string): string {
  switch (level) {
    case 'strong':
      return 'bg-green-100'
    case 'moderate':
      return 'bg-yellow-100'
    case 'needs_attention':
      return 'bg-orange-100'
    case 'critical':
      return 'bg-red-100'
    default:
      return 'bg-gray-100'
  }
}

function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'low':
      return 'bg-green-100 text-green-800 border-green-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'on_track':
      return 'text-green-600'
    case 'needs_attention':
      return 'text-yellow-600'
    case 'at_risk':
      return 'text-red-600'
    default:
      return 'text-gray-600'
  }
}

function getAchievementIcon(iconName: string) {
  switch (iconName) {
    case 'check-circle':
      return <CheckCircle className="h-5 w-5 text-green-500" />
    case 'shield':
      return <Shield className="h-5 w-5 text-blue-500" />
    case 'file-check':
      return <FileText className="h-5 w-5 text-purple-500" />
    case 'building':
      return <Building2 className="h-5 w-5 text-teal-500" />
    case 'file-text':
      return <FileText className="h-5 w-5 text-indigo-500" />
    default:
      return <Award className="h-5 w-5 text-yellow-500" />
  }
}

// ============================================================================
// POSTURE SCORE RING COMPONENT
// ============================================================================

interface PostureScoreRingProps {
  score: number
  size?: number
  strokeWidth?: number
  level: string
}

function PostureScoreRing({ score, size = 200, strokeWidth = 12, level }: PostureScoreRingProps) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  const getColor = () => {
    switch (level) {
      case 'strong':
        return '#10B981'
      case 'moderate':
        return '#F59E0B'
      case 'needs_attention':
        return '#F97316'
      case 'critical':
        return '#EF4444'
      default:
        return '#6B7280'
    }
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-5xl font-bold ${getPostureLevelColor(level)}`}>
          {score}
        </span>
        <span className="text-sm text-gray-500 uppercase tracking-wide">
          out of 100
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// KPI CARD COMPONENT
// ============================================================================

interface KPICardProps {
  title: string
  value: number | string
  subtitle?: string
  trend?: number
  trendDirection?: 'up' | 'down' | 'stable'
  icon: React.ReactNode
  color: string
  href?: string
}

function KPICard({ title, value, subtitle, trend, trendDirection, icon, color, href }: KPICardProps) {
  const content = (
    <div className={`card p-6 ${href ? 'hover:shadow-lg transition-shadow cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-sm font-medium ${
            trendDirection === 'up' ? 'text-green-600' :
            trendDirection === 'down' ? 'text-red-600' :
            'text-gray-500'
          }`}>
            {trendDirection === 'up' && <TrendingUp className="h-4 w-4 mr-1" />}
            {trendDirection === 'down' && <TrendingDown className="h-4 w-4 mr-1" />}
            {trendDirection === 'stable' && <Minus className="h-4 w-4 mr-1" />}
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )

  if (href) {
    return <Link to={href}>{content}</Link>
  }
  return content
}

// ============================================================================
// MINI SPARKLINE COMPONENT
// ============================================================================

interface SparklineProps {
  data: Array<{ date: string; evidence_count: number }>
  height?: number
}

function Sparkline({ data, height = 60 }: SparklineProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        No trend data
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
        <defs>
          <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="evidence_count"
          stroke="#3B82F6"
          strokeWidth={2}
          fill="url(#sparklineGradient)"
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '12px',
          }}
          labelFormatter={(label) => new Date(label).toLocaleDateString()}
          formatter={(value: number) => [`${value} items`, 'Evidence']}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// ============================================================================
// MAIN EXECUTIVE DASHBOARD COMPONENT
// ============================================================================

export default function ExecutiveDashboard() {
  const [data, setData] = useState<ExecutiveData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [comparePeriod, setComparePeriod] = useState<ComparePeriod>('month')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true)

    try {
      const response = await api.getExecutiveDashboard(comparePeriod)
      if (response.success) {
        setData(response.executive)
      } else {
        setError('Failed to load executive dashboard')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load executive dashboard')
    } finally {
      setLoading(false)
      if (isRefresh) setIsRefreshing(false)
    }
  }, [comparePeriod])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleExportPDF = async () => {
    setIsExporting(true)
    try {
      // Use browser print functionality for PDF export
      window.print()
    } finally {
      setIsExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading executive dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-700 font-medium">{error}</p>
        <button
          onClick={() => fetchData()}
          className="mt-4 btn-primary"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!data) return null

  const periodLabels: Record<ComparePeriod, string> = {
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    year: 'This Year',
  }

  return (
    <div className="space-y-6 print:space-y-4" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Executive Dashboard</h1>
            <p className="text-gray-500">
              Security posture overview for {data.organization.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(['week', 'month', 'quarter', 'year'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setComparePeriod(period)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  comparePeriod === period
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
          {/* Refresh Button */}
          <button
            onClick={() => fetchData(true)}
            disabled={isRefreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          {/* Export Button */}
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block">
        <div className="flex items-center justify-between border-b pb-4 mb-6">
          <div className="flex items-center gap-3">
            <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Executive Security Report</h1>
              <p className="text-sm text-gray-500">{data.organization.name}</p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p>Generated: {new Date(data.generated_at).toLocaleDateString()}</p>
            <p>Period: {periodLabels[comparePeriod]}</p>
          </div>
        </div>
      </div>

      {/* Overall Security Posture */}
      <div className={`card p-8 ${getPostureLevelBg(data.posture.level)}`}>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Score Ring */}
          <div className="flex-shrink-0">
            <PostureScoreRing
              score={data.posture.score}
              level={data.posture.level}
            />
          </div>

          {/* Posture Details */}
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Security Posture: {data.posture.level.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h2>
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
              {data.posture.trend === 'improving' && (
                <>
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span className="text-green-600 font-medium">Trend: Improving</span>
                </>
              )}
              {data.posture.trend === 'stable' && (
                <>
                  <Minus className="h-5 w-5 text-gray-600" />
                  <span className="text-gray-600 font-medium">Trend: Stable</span>
                </>
              )}
              {data.posture.trend === 'declining' && (
                <>
                  <TrendingDown className="h-5 w-5 text-red-600" />
                  <span className="text-red-600 font-medium">Trend: Declining</span>
                </>
              )}
            </div>
            <p className="text-gray-600 max-w-lg">
              {data.posture.level === 'strong' && 'Your organization demonstrates excellent security practices with strong compliance and minimal risk exposure.'}
              {data.posture.level === 'moderate' && 'Your organization has a reasonable security posture with some areas requiring attention.'}
              {data.posture.level === 'needs_attention' && 'Several security areas require immediate attention to improve your overall posture.'}
              {data.posture.level === 'critical' && 'Critical security gaps have been identified that require urgent remediation.'}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="flex-shrink-0 grid grid-cols-2 gap-4 text-center">
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{data.kpis.compliance.compliant_controls}</p>
              <p className="text-xs text-gray-600">of {data.kpis.compliance.total_controls} Controls</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{data.kpis.risk.open}</p>
              <p className="text-xs text-gray-600">Open Risks</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{data.kpis.vendor.assessed}</p>
              <p className="text-xs text-gray-600">of {data.kpis.vendor.total} Vendors</p>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900">{data.kpis.activity.evidence_this_period}</p>
              <p className="text-xs text-gray-600">Evidence Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 print:grid-cols-4">
        <KPICard
          title="Compliance Score"
          value={`${data.kpis.compliance.score}%`}
          subtitle={`${data.kpis.compliance.compliant_controls}/${data.kpis.compliance.total_controls} controls`}
          trend={data.kpis.compliance.trend}
          trendDirection={data.kpis.compliance.trend_direction}
          icon={<Shield className="h-6 w-6 text-blue-600" />}
          color="bg-blue-100"
          href="/controls"
        />
        <KPICard
          title="Risk Posture"
          value={`${data.kpis.risk.posture_score}%`}
          subtitle={`${data.kpis.risk.critical} critical, ${data.kpis.risk.high} high`}
          icon={<AlertTriangle className="h-6 w-6 text-orange-600" />}
          color="bg-orange-100"
          href="/risks"
        />
        <KPICard
          title="Vendor Health"
          value={`${data.kpis.vendor.health_score}%`}
          subtitle={`${data.kpis.vendor.assessed}/${data.kpis.vendor.total} assessed`}
          icon={<Building2 className="h-6 w-6 text-teal-600" />}
          color="bg-teal-100"
          href="/tprm"
        />
        <KPICard
          title="Activity"
          value={data.kpis.activity.evidence_this_period}
          subtitle={`Evidence ${periodLabels[comparePeriod].toLowerCase()}`}
          trend={data.kpis.activity.evidence_trend}
          trendDirection={data.kpis.activity.evidence_trend > 0 ? 'up' : data.kpis.activity.evidence_trend < 0 ? 'down' : 'stable'}
          icon={<FileText className="h-6 w-6 text-purple-600" />}
          color="bg-purple-100"
          href="/evidence"
        />
      </div>

      {/* Top Risks & Overdue Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
        {/* Top Risks */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
              Top Risks Requiring Attention
            </h3>
            <Link
              to="/risks"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center print:hidden"
            >
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {data.top_risks.length > 0 ? (
            <div className="space-y-3">
              {data.top_risks.map((risk, index) => (
                <div
                  key={risk.id}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {risk.title}
                      </p>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getSeverityColor(risk.severity)}`}>
                        {risk.severity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                      {risk.category && <span>{risk.category}</span>}
                      {risk.owner && (
                        <>
                          <span className="text-gray-300">|</span>
                          <Users className="h-3 w-3" />
                          <span>{risk.owner}</span>
                        </>
                      )}
                      {risk.days_overdue !== null && risk.days_overdue > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-red-600">{risk.days_overdue}d overdue</span>
                        </>
                      )}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-sm font-bold text-gray-700">
                    {risk.risk_score}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-500">No open risks</p>
            </div>
          )}
        </div>

        {/* Overdue Controls */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-red-500" />
              Overdue Controls
            </h3>
            <Link
              to="/controls?status=overdue"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center print:hidden"
            >
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {data.overdue_controls.length > 0 ? (
            <div className="space-y-3">
              {data.overdue_controls.map((control, index) => (
                <div
                  key={control.id}
                  className="flex items-start gap-3 p-3 bg-red-50 rounded-lg"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-red-200 rounded-full flex items-center justify-center text-sm font-medium text-red-700">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {control.control_number}: {control.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                      <span>{control.framework}</span>
                      {control.owner && (
                        <>
                          <span className="text-gray-300">|</span>
                          <Users className="h-3 w-3" />
                          <span>{control.owner}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <span className="flex-shrink-0 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                    {control.days_overdue}d overdue
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-500">No overdue controls</p>
            </div>
          )}
        </div>
      </div>

      {/* Achievements & Framework Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
        {/* Achievements */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
            <Award className="h-5 w-5 mr-2 text-yellow-500" />
            Achievements {periodLabels[comparePeriod]}
          </h3>
          {data.achievements.length > 0 ? (
            <div className="space-y-3">
              {data.achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-lg"
                >
                  {getAchievementIcon(achievement.icon)}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {achievement.title}
                    </p>
                    <p className="text-xs text-gray-500">{achievement.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No achievements yet this {comparePeriod}</p>
            </div>
          )}
        </div>

        {/* Framework Status */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-blue-500" />
              Framework Status
            </h3>
            <Link
              to="/frameworks"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center print:hidden"
            >
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {data.frameworks.length > 0 ? (
            <div className="space-y-3">
              {data.frameworks.map((framework) => (
                <div key={framework.id} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {framework.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${getStatusColor(framework.status)}`}>
                        {framework.compliance_percentage}%
                      </span>
                      {framework.overdue_controls > 0 && (
                        <span className="text-xs text-red-600">
                          ({framework.overdue_controls} overdue)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        framework.compliance_percentage >= 80
                          ? 'bg-green-500'
                          : framework.compliance_percentage >= 60
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${framework.compliance_percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {framework.compliant_controls}/{framework.total_controls} controls compliant
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No frameworks enabled</p>
            </div>
          )}
        </div>
      </div>

      {/* Activity Trend */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-indigo-500" />
            30-Day Activity Trend
          </h3>
        </div>
        <div className="h-32">
          <Sparkline data={data.trend} height={120} />
        </div>
      </div>

      {/* Footer - Print Only */}
      <div className="hidden print:block border-t pt-4 mt-8 text-center text-xs text-gray-500">
        <p>Confidential - Generated by Clarantis GRC Platform</p>
        <p>Report Date: {new Date().toLocaleDateString()} | Period: {periodLabels[comparePeriod]}</p>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .card {
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  )
}
